import { useEffect, useRef } from "react";
import { MbApiLogTailSchema } from "#shared/schemas/mbApiLog.ts";
import { SpotifyApiLogTailSchema } from "#shared/schemas/spotifyApiLog.ts";
import { useIsAdmin, useMe } from "@web/features/auth/useMe.ts";
import { useAppStatus } from "@web/features/library/api.ts";
import { api } from "@web/lib/api.ts";
import { coyLog, logAppStatusSnapshot } from "@web/lib/coyLog.ts";

const POLL_MS = 1_500;

/**
 * Streams server Spotify + MusicBrainz API call lines + status snapshots into
 * the Chrome console (filter by `coykoi`). API call logs are admin-only debug;
 * every user still gets status snapshots.
 */
export function useCoyKoiLiveConsole(): void {
  const me = useMe();
  const isAdmin = useIsAdmin();
  const status = useAppStatus({ refetchInterval: 15_000 });
  const lastSpotifySeq = useRef(0);
  const lastMbSeq = useRef(0);
  const lastFingerprint = useRef<string | null>(null);
  const booted = useRef(false);

  useEffect(() => {
    if (!booted.current) {
      booted.current = true;
      coyLog.info(
        "Live console on — filter DevTools by “coykoi”. Logs: data/logs/spotify-api.jsonl · musicbrainz-api.jsonl",
      );
    }
  }, []);

  useEffect(() => {
    if (!status.data) return;
    const s = status.data;
    const fingerprint = [
      s.connected ? "1" : "0",
      s.api_ok ? "1" : "0",
      s.user?.id ?? "",
      String(s.completeness.liked),
      String(s.completeness.playlists_listed),
      String(s.completeness.unique_tracks),
      s.quota.active ? s.quota.ready_at ?? "1" : "0",
      String(s.api_today.web_api_calls),
      s.sync?.id ?? "",
      s.error ?? "",
    ].join("|");
    if (fingerprint === lastFingerprint.current) return;
    lastFingerprint.current = fingerprint;
    logAppStatusSnapshot(s, status.isFetching ? "refresh" : "update");
  }, [status.data, status.isFetching]);

  useEffect(() => {
    // Wait until /api/me resolves so we don't 403-spam as a non-admin.
    if (me.isPending || !isAdmin) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function tick(): Promise<void> {
      try {
        const [spotifyRaw, mbRaw] = await Promise.all([
          api(`/api/spotify/log?after=${lastSpotifySeq.current}`),
          api(`/api/mb/log?after=${lastMbSeq.current}`),
        ]);
        if (cancelled) return;

        const spotify = SpotifyApiLogTailSchema.parse(spotifyRaw);
        for (const entry of spotify.entries) {
          coyLog.api(entry);
          if (entry.seq > lastSpotifySeq.current) lastSpotifySeq.current = entry.seq;
        }

        const mb = MbApiLogTailSchema.parse(mbRaw);
        for (const entry of mb.entries) {
          coyLog.mb(entry);
          if (entry.seq > lastMbSeq.current) lastMbSeq.current = entry.seq;
        }
      } catch (err) {
        if (!cancelled) {
          coyLog.error(
            "api log poll failed",
            err instanceof Error ? err.message : err,
          );
        }
      } finally {
        if (!cancelled) timer = setTimeout(() => void tick(), POLL_MS);
      }
    }

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isAdmin, me.isPending]);
}
